
import { useCallback, useEffect, useMemo, useState } from "react";
import { occupancyService } from "../services/occupancyService";
import { overlaps } from "../utils/timelineUtils";

export default function useOccupancy(
  hostelId = null,
  startDate = null,
  endDate = null
) {
  const [rooms, setRooms] = useState([]);
  const [stays, setStays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Load occupancy for the selected hostel and Gantt date range
  const refresh = useCallback(async () => {
    if (!startDate || !endDate || startDate > endDate) {
      setLoading(true);
      return;
    }

    setLoading(true);

    try {
      const data = await occupancyService.getOccupancy(
        hostelId,
        startDate,
        endDate
      );

      setRooms(data.rooms || []);
      setStays(data.stays || []);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load occupancy");
      setRooms([]);
      setStays([]);
    } finally {
      setLoading(false);
    }
  }, [hostelId, startDate, endDate]);

  // Reload when the hostel or Gantt date range changes
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Validate room and booking dates
  const validate = useCallback(
    (v) => {
      const room = rooms.find((r) => r.id === v.roomId);

      if (!room) {
        throw new Error("Select a room.");
      }

      if (room.status === "MAINTENANCE") {
        throw new Error("Room is marked for maintenance.");
      }

      if (
        !Number.isFinite(Number(v.guests)) ||
        Number(v.guests || 1) < 1
      ) {
        throw new Error("Number of guests must be at least 1.");
      }

      if (Number(v.guests || 1) > Number(room.capacity)) {
        throw new Error(
          `Room ${room.number} capacity is ${room.capacity}.`
        );
      }

      if (!v.checkIn || !v.checkOut || v.checkOut <= v.checkIn) {
        throw new Error(
          "Check-out date must be after check-in date."
        );
      }

      const conflict = stays.some(
        (s) =>
          s.id !== v.id &&
          s.roomId === v.roomId &&
          s.status !== "CANCELLED" &&
          overlaps(
            v.checkIn,
            v.checkOut,
            s.checkIn,
            s.checkOut
          )
      );

      if (conflict) {
        throw new Error(
          "Room already has a booking during these dates."
        );
      }
    },
    [rooms, stays]
  );

  
  // Update an existing stay or move it to another room
  const updateStay = useCallback(
  async (v) => {
    const operation = String(v.operation || "").toUpperCase();

    if (operation !== "MOVE") {
      validate(v);
    }

    const saved = await occupancyService.updateStay(v);

    // Refresh Gantt after successful update
    await refresh();

    return saved;
  },
  [validate, refresh]
);


  // Create a new stay
  const createStay = useCallback(
    async (v) => {
      validate(v);

      const saved = await occupancyService.createStay(v);

      setStays((current) => [...current, saved]);

      return saved;
    },
    [validate]
  );

  // Delete a stay
  const deleteStay = useCallback(async (id) => {
    await occupancyService.deleteStay(id);

    setStays((current) =>
      current.filter((s) => s.id !== id)
    );
  }, []);

  // Update a room
  const updateRoom = useCallback(async (v) => {
    const saved = await occupancyService.updateRoom(v);

    setRooms((current) =>
      current.map((r) =>
        r.id === saved.id ? { ...r, ...saved } : r
      )
    );

    return saved;
  }, []);

  // Summary for the currently loaded hostel
  const summary = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);

    const checkedInRoomIds = new Set(
      stays
        .filter((s) => s.status === "CHECKED_IN")
        .map((s) => s.roomId)
    );

    return {
      totalRooms: rooms.length,

      occupiedRooms: rooms.filter((r) =>
        checkedInRoomIds.has(r.id)
      ).length,

      availableRooms: rooms.filter(
        (r) =>
          r.status === "AVAILABLE" &&
          !checkedInRoomIds.has(r.id)
      ).length,

      arrivals: stays.filter(
        (s) =>
          s.checkIn === today &&
          s.status !== "CANCELLED"
      ).length,

      departures: stays.filter(
        (s) =>
          s.checkOut === today &&
          s.status === "CHECKED_IN"
      ).length,
    };
  }, [rooms, stays]);

  return {
    rooms,
    stays,
    loading,
    error,
    summary,
    refresh,
    updateStay,
    createStay,
    deleteStay,
    updateRoom,
  };
}
